import React, { Component } from "react";
import { AxiosResponse } from "axios";
import { getRequest } from "../../utils/httpRequest";
import { errorNotification } from "../../utils/helpers";
import NewsletterForm from "../Newsletter/NewsletterForm";

type Kind = "weekend_reads" | "weekday_wisdom" | "podcast" | "magpies" | "other";

interface Issue {
  id: number;
  kind: Kind;
  kind_label: string;
  issue_number?: number | null;
  subject: string;
  headline: string;
  blurb?: string | null;
  sent_at: string;
  archive_url: string;
}

interface Pagination {
  current_page: number;
  next_page?: number;
  total_pages: number;
  total_count: number;
}

interface IssuesResponse {
  issues: Issue[];
  counts: Record<Kind, number>;
  pagination: Pagination;
}

interface State {
  loading: boolean;
  searching: boolean;
  failed: boolean;
  issues: Issue[];
  counts: Partial<Record<Kind, number>>;
  pagination: Pagination;
  query: string;
  kind: Kind | "";
}

const SEARCH_DEBOUNCE_MS = 350;
// While the archive is being filled for the first time, look again every
// few seconds rather than leaving the first visitor on an empty page.
const FILL_POLL_MS = 5000;
const FILL_POLL_LIMIT = 24;

const KIND_TABS: { kind: Kind | ""; label: string }[] = [
  { kind: "", label: "Everything" },
  { kind: "weekend_reads", label: "Weekend Reads" },
  { kind: "weekday_wisdom", label: "Weekday Wisdom" },
  { kind: "podcast", label: "Podcasts" },
  { kind: "magpies", label: "Magpies" },
  { kind: "other", label: "Everything else" },
];

const KINDS = KIND_TABS.map((tab) => tab.kind).filter(Boolean);

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const yearOf = (iso: string) => new Date(iso).getFullYear();

// Which page numbers to show: first, last, and a window around the current
// one, with gaps marked. 1 … 5 6 7 … 26.
const pageNumbers = (current: number, total: number): (number | "gap")[] => {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const around = [current - 1, current, current + 1].filter(
    (n) => n > 1 && n < total,
  );
  const numbers: (number | "gap")[] = [1];
  if (around[0] > 2) numbers.push("gap");
  numbers.push(...around);
  if (around[around.length - 1] < total - 1) numbers.push("gap");
  numbers.push(total);

  return numbers;
};

// State that survives a reload or a shared link: ?kind=weekend_reads&q=ai&page=3
const readUrlState = (): { kind: Kind | ""; query: string; page: number } => {
  const params = new URLSearchParams(window.location.search);
  const kind = params.get("kind") || "";
  const page = parseInt(params.get("page") || "1", 10);

  return {
    kind: KINDS.includes(kind as Kind) ? (kind as Kind) : "",
    query: params.get("q") || "",
    page: Number.isFinite(page) && page > 0 ? page : 1,
  };
};

// The archive of every newsletter sent from Mailchimp. Each row links to
// Mailchimp's own "view in browser" copy, so the site never has to store the
// emails themselves. Same bones as the blog index: hero, search, a page of
// results at a time.
class NewslettersIndex extends Component<{}, State> {
  private requestSeq = 0;
  private searchTimer?: number;
  private fillTimer?: number;
  private fillPolls = 0;
  private listRef = React.createRef<HTMLDivElement>();

  state: State = {
    loading: true,
    searching: false,
    failed: false,
    issues: [],
    counts: {},
    pagination: { current_page: 1, total_pages: 1, total_count: 0 },
    query: readUrlState().query,
    kind: readUrlState().kind,
  };

  componentDidMount() {
    document.title = "Weekend Reads & Weekday Wisdom archive | 42courses";
    this.fetchIssues(readUrlState().page);
  }

  componentWillUnmount() {
    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }
    if (this.fillTimer) {
      window.clearTimeout(this.fillTimer);
    }
  }

  // Nothing in the archive at all (not just no matches): the server has just
  // queued the first fill, so come back for it.
  scheduleFillPoll = (counts: Partial<Record<Kind, number>>) => {
    const total = Object.values(counts).reduce((sum, count) => sum + (count || 0), 0);
    if (total > 0 || this.fillPolls >= FILL_POLL_LIMIT) return;

    this.fillPolls += 1;
    this.fillTimer = window.setTimeout(() => this.fetchIssues(1), FILL_POLL_MS);
  };

  syncUrl = (page: number) => {
    const params = new URLSearchParams();
    if (this.state.kind) params.set("kind", this.state.kind);
    if (this.state.query.trim()) params.set("q", this.state.query.trim());
    if (page > 1) params.set("page", String(page));
    const search = params.toString();

    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${search ? `?${search}` : ""}`,
    );
  };

  fetchIssues = (page = 1, scrollToList = false) => {
    const seq = ++this.requestSeq;
    const { kind } = this.state;
    const query = this.state.query.trim();
    const params = [`page=${page}`];
    if (kind) params.push(`kind=${kind}`);
    if (query) params.push(`q=${encodeURIComponent(query)}`);

    this.setState({ searching: true, failed: false });
    this.syncUrl(page);

    getRequest(`/api/newsletter-issues.json?${params.join("&")}`)
      .then((response: AxiosResponse<IssuesResponse>) => {
        if (seq !== this.requestSeq) return;

        const { issues, counts, pagination } = response.data;

        this.setState(
          { issues, counts, pagination, loading: false, searching: false },
          () => {
            this.scheduleFillPoll(counts);
            if (scrollToList && this.listRef.current) {
              this.listRef.current.scrollIntoView({ block: "start" });
            }
          },
        );
      })
      .catch((error) => {
        if (seq !== this.requestSeq) return;

        this.setState({ loading: false, searching: false, failed: true });
        errorNotification(
          "Error loading the newsletter archive",
          JSON.stringify(error.response?.data || error.message),
        );
      });
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ query: event.target.value });

    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }

    this.searchTimer = window.setTimeout(
      () => this.fetchIssues(1),
      SEARCH_DEBOUNCE_MS,
    );
  };

  handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (this.searchTimer) {
      window.clearTimeout(this.searchTimer);
    }

    this.fetchIssues(1);
  };

  selectKind = (kind: Kind | "") => {
    if (kind === this.state.kind) return;

    this.setState({ kind }, () => this.fetchIssues(1));
  };

  goToPage = (page: number) => {
    const { current_page, total_pages } = this.state.pagination;
    if (page === current_page || page < 1 || page > total_pages) return;

    this.fetchIssues(page, true);
  };

  renderBadge = (issue: Issue) => {
    const label =
      issue.issue_number && issue.kind !== "other"
        ? `${issue.kind_label} #${issue.issue_number}`
        : issue.kind_label;

    return (
      <span className={`newsletters-page__badge is-${issue.kind}`}>
        {label}
      </span>
    );
  };

  renderIssue = (issue: Issue) => (
    <li className="newsletters-page__issue" key={issue.id}>
      <a
        href={issue.archive_url}
        target="_blank"
        rel="noopener noreferrer"
        className="newsletters-page__issue-link"
      >
        <div className="newsletters-page__issue-meta">
          {this.renderBadge(issue)}
          <time dateTime={issue.sent_at}>{formatDate(issue.sent_at)}</time>
        </div>
        <h3 className="newsletters-page__headline">{issue.headline}</h3>
        {issue.blurb && (
          <p className="newsletters-page__blurb">{issue.blurb}</p>
        )}
        <span className="newsletters-page__read" aria-hidden="true">
          Read it →
        </span>
      </a>
    </li>
  );

  renderList = () => {
    const { issues } = this.state;
    const groups: { year: number; issues: Issue[] }[] = [];

    issues.forEach((issue) => {
      const year = yearOf(issue.sent_at);
      const current = groups[groups.length - 1];
      if (current && current.year === year) {
        current.issues.push(issue);
      } else {
        groups.push({ year, issues: [issue] });
      }
    });

    return groups.map((group) => (
      <section className="newsletters-page__year" key={group.year}>
        <h2>{group.year}</h2>
        <ul className="newsletters-page__list">
          {group.issues.map(this.renderIssue)}
        </ul>
      </section>
    ));
  };

  renderPagination = () => {
    const { current_page, total_pages, total_count } = this.state.pagination;
    if (total_pages <= 1) return null;

    return (
      <nav className="newsletters-page__pages" aria-label="Archive pages">
        <button
          type="button"
          className="newsletters-page__page-step"
          onClick={() => this.goToPage(current_page - 1)}
          disabled={current_page <= 1}
        >
          ← Newer
        </button>
        <ol className="newsletters-page__page-list">
          {pageNumbers(current_page, total_pages).map((entry, index) =>
            entry === "gap" ? (
              <li key={`gap-${index}`} className="newsletters-page__page-gap" aria-hidden="true">
                …
              </li>
            ) : (
              <li key={entry}>
                <button
                  type="button"
                  className={`newsletters-page__page ${
                    entry === current_page ? "is-current" : ""
                  }`}
                  aria-current={entry === current_page ? "page" : undefined}
                  onClick={() => this.goToPage(entry)}
                >
                  {entry}
                </button>
              </li>
            ),
          )}
        </ol>
        <button
          type="button"
          className="newsletters-page__page-step"
          onClick={() => this.goToPage(current_page + 1)}
          disabled={current_page >= total_pages}
        >
          Older →
        </button>
        <p className="newsletters-page__page-count">
          Page {current_page} of {total_pages} · {total_count.toLocaleString("en-GB")} emails
        </p>
      </nav>
    );
  };

  renderEmpty = () => {
    const { searching, failed, query, counts } = this.state;
    const total = Object.values(counts).reduce(
      (sum, count) => sum + (count || 0),
      0,
    );

    if (searching) {
      return (
        <div className="ui active centered inline loader newsletters-page__loader" />
      );
    }

    if (failed) {
      return (
        <p>
          The archive could not be loaded just now.{" "}
          <button
            type="button"
            className="newsletters-page__retry"
            onClick={() => this.fetchIssues(this.state.pagination.current_page)}
          >
            Try again
          </button>
        </p>
      );
    }

    if (total === 0) {
      return (
        <p>
          The archive is being filled from Mailchimp right now. This page will
          update itself in a moment.
        </p>
      );
    }

    return (
      <p>
        Nothing found{query.trim() ? <> for “{query.trim()}”</> : null}. Try
        another word, or a Weekend Reads issue number.
      </p>
    );
  };

  render() {
    const { loading, issues, query, kind, counts, searching } = this.state;

    if (loading) {
      return (
        <main className="newsletters-page">
          <div className="ui active inverted dimmer">
            <div className="ui text loader">Loading</div>
          </div>
        </main>
      );
    }

    const total = Object.values(counts).reduce(
      (sum, count) => sum + (count || 0),
      0,
    );

    return (
      <div className="newsletters-page">
        <header className="newsletters-page__hero">
          <span className="newsletters-page__watermark" aria-hidden="true">
            42
          </span>
          <h1 className="newsletters-page__intro">
            Every <em>Weekend Reads</em>, Weekday Wisdom and podcast we have
            ever sent. In one place, at last.
          </h1>
          <p className="newsletters-page__sub">
            Interesting things that caught our eye, every week since 2020.
          </p>

          <form
            className="newsletters-page__search"
            role="search"
            onSubmit={this.handleSearchSubmit}
          >
            <i className="search icon" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={this.handleSearchChange}
              placeholder="Search by topic, or an issue number…"
              aria-label="Search the newsletter archive"
            />
          </form>

          <nav className="newsletters-page__tabs" aria-label="Newsletter type">
            {KIND_TABS.map((tab) => {
              const count = tab.kind ? counts[tab.kind] || 0 : total;
              if (tab.kind && count === 0) return null;

              return (
                <button
                  type="button"
                  key={tab.kind || "all"}
                  className={`newsletters-page__tab ${
                    kind === tab.kind ? "is-active" : ""
                  }`}
                  aria-pressed={kind === tab.kind}
                  onClick={() => this.selectKind(tab.kind)}
                >
                  {tab.label}
                  <span className="newsletters-page__tab-count">{count}</span>
                </button>
              );
            })}
          </nav>
        </header>

        <div className="newsletters-page__body">
          <main
            className={`newsletters-page__main ${searching ? "is-searching" : ""}`}
            ref={this.listRef}
          >
            {kind === "podcast" && (
              <a className="newsletters-page__note" href="/podcasts">
                <span className="newsletters-page__note-kicker">
                  Looking for the latest episodes?
                </span>
                <span className="newsletters-page__note-text">
                  Not every episode gets its own email, but every one of them
                  is on the podcast page, playable, with no inbox required.
                </span>
                <span className="newsletters-page__note-cta">
                  Go to the podcast page &rarr;
                </span>
              </a>
            )}
            {issues.length === 0 ? (
              <div className="newsletters-page__empty">{this.renderEmpty()}</div>
            ) : (
              <>
                {this.renderList()}
                {this.renderPagination()}
              </>
            )}
          </main>

          <aside className="newsletters-page__signup" aria-label="Sign up">
            <div className="newsletters-page__signup-card">
              <NewsletterForm
                heading={
                  <>
                    <p className="newsletters-page__signup-kicker">
                      Not on the list yet?
                    </p>
                    <h2 className="newsletters-page__signup-heading">
                      Weekend Reads every Friday, and Weekday Wisdom every
                      Tuesday.
                    </h2>
                  </>
                }
                promise="Free, and you can leave any time."
                buttonLabel="Sign up for Weekend Reads"
              />
            </div>
          </aside>
        </div>
      </div>
    );
  }
}

export default NewslettersIndex;
